import { computed, reactive } from 'vue';
import {
  generateAutomationPayload,
  getDefaultConditions,
} from 'dashboard/helper/automationHelper';
import { validateAutomation } from 'dashboard/helper/validations';
import {
  DEFAULT_TRIGGER,
  DEFAULT_WAIT_STATUS,
  MAX_DELAY_MINUTES,
  MIN_DELAY_MINUTES,
  buildWaitConditions,
  composeDelayedConditions,
  defaultWaitCondition,
  findTrigger,
  isWaitAttributeAllowed,
  parseDelayedRule,
  unitForDelay,
} from 'dashboard/routes/dashboard/settings/automation/delayedAutomation';

export const DELAY_OUT_OF_RANGE = 'DELAY_OUT_OF_RANGE';

/**
 * State and payload logic for the "wait before running" part of the automation modals.
 * In delayed mode the chosen trigger owns the event and one managed condition (status or message
 * type); automation.conditions holds the rule's other conditions. Like an instant rule, a delayed
 * rule needs at least one of them.
 * @param {import('vue').Ref} automation - the automation ref from useAutomation
 */
export function useAutomationWait(automation) {
  const wait = reactive({
    enabled: false,
    trigger: DEFAULT_TRIGGER,
    status: DEFAULT_WAIT_STATUS,
    delay: 60,
    unit: 'hours',
  });

  const isMessageWait = computed(() =>
    Boolean(findTrigger(wait.trigger).messageType)
  );

  const newCondition = () =>
    wait.enabled
      ? [defaultWaitCondition(wait.trigger)]
      : getDefaultConditions(automation.value.event_name);

  const setWaitEnabled = enabled => {
    wait.enabled = enabled;
    if (enabled)
      automation.value.event_name = findTrigger(wait.trigger).eventName;
    automation.value.conditions = newCondition();
  };

  // Keep the conditions the new trigger still accepts, and always at least one.
  const setTrigger = triggerKey => {
    wait.trigger = triggerKey;
    automation.value.event_name = findTrigger(triggerKey).eventName;
    const kept = automation.value.conditions.filter(item =>
      isWaitAttributeAllowed(triggerKey, item.attribute_key)
    );
    automation.value.conditions = kept.length ? kept : newCondition();
  };

  // Opens a saved rule: the managed condition goes to the wait controls, the rest stay editable.
  const hydrateWait = (rule, formattedConditions) => {
    const parsed = parseDelayedRule(rule);
    if (!parsed) return;

    Object.assign(wait, {
      enabled: true,
      trigger: parsed.trigger,
      status: parsed.status,
      delay: parsed.delay,
      unit: unitForDelay(parsed.delay),
    });
    automation.value.event_name = findTrigger(parsed.trigger).eventName;
    const conditions = parsed.extraIndexes
      .map(index => formattedConditions[index])
      .filter(Boolean);
    automation.value.conditions = conditions.length
      ? conditions
      : newCondition();
  };

  // The event's default condition may be one the wait manages, so a new row starts on an
  // attribute the condition list offers.
  const addCondition = () => {
    automation.value.conditions = [
      ...automation.value.conditions,
      ...newCondition(),
    ];
  };

  // The managed attribute is set by the wait controls; status waits take only STATUS_WAIT_ATTRIBUTES.
  const filterWaitAttributes = attributes =>
    wait.enabled
      ? attributes.filter(attribute =>
          isWaitAttributeAllowed(wait.trigger, attribute.key)
        )
      : attributes;

  const validateWithWait = () => {
    const errors = validateAutomation(automation.value);
    if (
      wait.enabled &&
      (!Number.isInteger(wait.delay) ||
        wait.delay < MIN_DELAY_MINUTES ||
        wait.delay > MAX_DELAY_MINUTES)
    ) {
      errors.execution_delay = DELAY_OUT_OF_RANGE;
    }
    return errors;
  };

  const buildPayloadWithWait = () => {
    const payload = generateAutomationPayload(automation.value);
    if (!wait.enabled) return { ...payload, execution_delay: null };

    return {
      ...payload,
      event_name: findTrigger(wait.trigger).eventName,
      conditions: composeDelayedConditions(
        buildWaitConditions(wait),
        payload.conditions
      ),
      execution_delay: wait.delay,
    };
  };

  return {
    wait,
    isMessageWait,
    setWaitEnabled,
    setTrigger,
    hydrateWait,
    addCondition,
    filterWaitAttributes,
    validateWithWait,
    buildPayloadWithWait,
  };
}
